import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { api, ErreurApi, lire } from '../api'
import { BoutonClaude } from './BoutonClaude'
import { formaterInstant } from './Historique'
import { Icone } from './Icone'

// Récupérer la pièce (décision du 11/10/2026) : un mail annonce un document sans le joindre.
// Un collecteur relève ce fournisseur : un gros bouton le lance, et la pièce rapportée termine la
// tâche (rapprochement des pièces annoncées, chaque minute, pour cette tâche et les autres).
// Aucun collecteur : un gros bouton ouvre Claude desktop (Cowork, lien claude://) avec le prompt
// prêt ; Claude passe par Claude in Chrome pour les sites où l'on est connecté. Avec un
// collecteur, Claude reste proposé en second, tant qu'aucune collecte n'est en cours.

const COLLECTE_OUVERTE = ['en_file', 'en_cours', 'attente_humain', 'reprise']

const STATUTS: Record<string, string> = {
  en_file: 'en file d’attente',
  en_cours: 'en cours',
  attente_humain: 'attend une vérification',
  reprise: 'reprend',
  terminee: 'terminée',
  echec: 'en échec',
  bloquee: 'bloquée',
  expiree: 'expirée',
  annulee: 'annulée',
}

/** Une collecte de moins de 30 minutes parle encore de cette tâche. */
const RECENTE_MS = 30 * 60_000

type Recuperation = {
  connexions: { collecte: { statut: string } | null; enLecture: number; lancable: boolean }[]
} | null

/** Une collecte qui tourne (ouverte, et qu'un lancement ne reprend pas). */
const tourne = (c: { collecte: { statut: string } | null; lancable: boolean }) =>
  Boolean(c.collecte && COLLECTE_OUVERTE.includes(c.collecte.statut) && !c.lancable)

/** Une collecte qui tourne, ou des documents rapportés encore en lecture. */
const actif = (d: Recuperation | undefined) =>
  Boolean(d?.connexions.some((c) => tourne(c) || c.enLecture > 0))

/** La recherche de la fiche d'une tâche qui attend un collecteur, relue pendant qu'il tourne. */
function useRecuperation(tacheId: string) {
  const client = useQueryClient()
  const r = useQuery({
    queryKey: ['taches', 'recuperation', tacheId],
    queryFn: () => lire(api.taches[':id'].recuperation.$get({ param: { id: tacheId } })),
    refetchInterval: (q) => (actif(q.state.data) ? 5_000 : false),
  })
  // Pendant une collecte ou la lecture de ses documents, la fiche est relue aussi : elle se
  // ferme quand ce qu'elle attend arrive.
  const suivre = actif(r.data)
  useEffect(() => {
    if (!suivre) return
    const minuterie = setInterval(
      () => void client.invalidateQueries({ queryKey: ['taches', 'fiche', tacheId], exact: true }),
      5_000,
    )
    return () => clearInterval(minuterie)
  }, [suivre, client, tacheId])
  return r
}

/** Lance le collecteur des connexions de la tâche ; rend le message et les refus à montrer. */
async function lancerPourTache(tacheId: string) {
  const res = await lire(api.taches[':id'].collecter.$post({ param: { id: tacheId } }))
  return {
    terminee: res.terminee,
    refus: res.refus.map((x) => `${x.connexion} : ${x.motif}`),
    lancees: res.lancees.length,
  }
}

export function RecupererPiece({ tacheId, ecriture }: { tacheId: string; ecriture: boolean }) {
  const client = useQueryClient()
  const [lance, setLance] = useState(false)
  const [occupe, setOccupe] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [refus, setRefus] = useState<string[]>([])
  const r = useRecuperation(tacheId)
  const d = r.data
  if (!d) return null

  const relevees = d.connexions.filter((c) => !c.sansReleve)
  if (relevees.length === 0)
    return (
      <section className="recuperer-piece">
        <h2>Récupérer la pièce</h2>
        <p className="discret">
          Aucun collecteur ne relève ce fournisseur. Claude lit le mail, retrouve le document dans
          votre Chrome (lien du mail ou espace client, où vous vous connectez vous-même), le range
          sur cette tâche et la termine. Le prompt est aussi copié.
        </p>
        {d.connexions.map((c) => (
          <p key={c.id} className="discret">
            {c.libelle} : {c.sansReleve}
          </p>
        ))}
        {d.prompt && <BoutonClaude prompt={d.prompt} taille="geant" />}
      </section>
    )

  const enCours = relevees.some(tourne)
  const enLecture = relevees.reduce((n, c) => n + c.enLecture, 0)
  const aVerifier = relevees.reduce((n, c) => n + c.aVerifier, 0)
  const recentes = relevees.filter(
    (c) => c.collecte && Date.now() - new Date(c.collecte.creeLe).getTime() < RECENTE_MS,
  )
  // Collecte finie, documents lus, et la tâche toujours ouverte : la pièce n'y était pas.
  const sansResultat =
    (lance || recentes.length > 0) &&
    !enCours &&
    enLecture === 0 &&
    recentes.every((c) => c.collecte && !COLLECTE_OUVERTE.includes(c.collecte.statut))
  const titre =
    relevees.length === 1
      ? `Lancer le collecteur « ${relevees[0]!.libelle} »`
      : `Lancer les ${relevees.length} collecteurs`

  async function lancer() {
    setMessage(null)
    setRefus([])
    setOccupe(true)
    try {
      const res = await lancerPourTache(tacheId)
      if (res.terminee) setMessage('La pièce était déjà arrivée : tâche terminée.')
      setRefus(res.refus)
      setLance(res.lancees > 0)
    } catch (e) {
      setMessage(e instanceof ErreurApi ? e.message : 'Collecte impossible.')
    }
    await client.invalidateQueries({ queryKey: ['taches', 'fiche', tacheId], exact: true })
    await r.refetch()
    setOccupe(false)
  }

  return (
    <section className="recuperer-piece">
      <h2>Récupérer la pièce par le collecteur</h2>
      <p className="discret">
        Le collecteur va chercher cette pièce et les autres documents récents du portail ; chaque
        tâche « récupérer la pièce » qu’ils couvrent se termine seule.
      </p>
      {ecriture && !enCours && enLecture === 0 && (
        <button
          type="button"
          className="bouton bouton-geant"
          disabled={occupe}
          onClick={() => void lancer()}
        >
          <Icone nom="download" taille={22} />
          {titre}
        </button>
      )}
      <ul className="recuperer-piece-connexions">
        {relevees.map((c) => (
          <li key={c.id}>
            <strong>{c.libelle}</strong>
            <span className="discret">
              {' '}
              ·{' '}
              {c.releve === 'ordinateur'
                ? `relevé par « ${c.ordinateur} »`
                : 'relevé par le serveur'}
              {c.collecte &&
                ` · collecte du ${formaterInstant(c.collecte.creeLe)} ${STATUTS[c.collecte.statut] ?? c.collecte.statut}${
                  c.collecte.statut === 'terminee'
                    ? ` (${c.collecte.documentsNouveaux} nouveau${c.collecte.documentsNouveaux > 1 ? 'x' : ''})`
                    : ''
                }`}
            </span>
            {c.collecte?.statut === 'en_file' && c.releve === 'ordinateur' && (
              <p className="discret">
                L’ordinateur « {c.ordinateur} » la prendra à son prochain tour, s’il est allumé.
              </p>
            )}
            {c.defi && (
              <p className="alerte">
                Le portail demande : {c.defi.libelle}. {c.titulaire} répond dans l’appli.
              </p>
            )}
            {c.collecte?.erreur && !COLLECTE_OUVERTE.includes(c.collecte.statut) && (
              <p className="discret">{c.collecte.erreur}</p>
            )}
          </li>
        ))}
      </ul>
      {enLecture > 0 && (
        <p className="discret" role="status">
          <Icone nom="hourglass_top" taille={18} /> {enLecture} document
          {enLecture > 1 ? 's' : ''} en lecture : la tâche se terminera seule si la pièce y est.
        </p>
      )}
      {aVerifier > 0 && (
        <p className="discret">
          {aVerifier} document{aVerifier > 1 ? 's' : ''} rapporté{aVerifier > 1 ? 's' : ''} à
          vérifier dans <Link to="/pieces">Pièces</Link>.
        </p>
      )}
      {message && <p className="alerte">{message}</p>}
      {refus.map((x) => (
        <p key={x} className="alerte">
          {x}
        </p>
      ))}
      {!enCours && enLecture === 0 && (
        <>
          <p className="discret">
            {sansResultat
              ? 'Collecte finie sans que la pièce soit reconnue : la traiter avec Claude.'
              : 'Ou la faire chercher par Claude (mail, espace client dans votre Chrome).'}
          </p>
          {d.prompt && <BoutonClaude prompt={d.prompt} />}
        </>
      )}
    </section>
  )
}

/**
 * Factures manquantes, vérification du portail (collecteurs à la demande, décision du
 * 21/10/2026) : le collecteur de la connexion, son état et le bouton qui le lance. Ce qu'il faut
 * faire est dit dans « À faire ici » ; la tâche se ferme seule quand la collecte a rapporté ce
 * qu'elle attend.
 */
export function CollecteurDeTache({ tacheId, ecriture }: { tacheId: string; ecriture: boolean }) {
  const client = useQueryClient()
  const [occupe, setOccupe] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [refus, setRefus] = useState<string[]>([])
  const r = useRecuperation(tacheId)
  const d = r.data
  if (!d || d.connexions.length === 0) return null
  const enCours = d.connexions.some(tourne)
  const lancables = d.connexions.filter((c) => c.lancable)

  async function lancer() {
    setMessage(null)
    setRefus([])
    setOccupe(true)
    try {
      const res = await lancerPourTache(tacheId)
      setRefus(res.refus)
      if (res.lancees > 0)
        setMessage('Collecteur lancé : la tâche se fermera seule si c’est réglé.')
    } catch (e) {
      setMessage(e instanceof ErreurApi ? e.message : 'Collecte impossible.')
    }
    await client.invalidateQueries({ queryKey: ['portails'] })
    await r.refetch()
    setOccupe(false)
  }

  return (
    <section className="recuperer-piece">
      <h2>Collecteur</h2>
      <ul className="recuperer-piece-connexions">
        {d.connexions.map((c) => (
          <li key={c.id}>
            <strong>{c.libelle}</strong>
            <span className="discret">
              {' '}
              ·{' '}
              {c.sansReleve ??
                (c.releve === 'ordinateur'
                  ? `relevé par « ${c.ordinateur} »`
                  : 'relevé par le serveur')}
              {c.collecte &&
                ` · collecte du ${formaterInstant(c.collecte.creeLe)} ${STATUTS[c.collecte.statut] ?? c.collecte.statut}`}
            </span>
            {c.defi && (
              <p className="alerte">
                Le portail demande : {c.defi.libelle}. {c.titulaire} répond dans l’appli.
              </p>
            )}
            {c.collecte?.erreur && !COLLECTE_OUVERTE.includes(c.collecte.statut) && (
              <p className="discret">{c.collecte.erreur}</p>
            )}
          </li>
        ))}
      </ul>
      {ecriture && !enCours && lancables.length > 0 && (
        <button
          type="button"
          className="bouton bouton-geant"
          disabled={occupe}
          onClick={() => void lancer()}
        >
          <Icone nom="play_arrow" taille={22} />
          {lancables.length === 1
            ? `Lancer le collecteur « ${lancables[0]!.libelle} »`
            : `Lancer les ${lancables.length} collecteurs`}
        </button>
      )}
      {enCours && (
        <p className="discret" role="status">
          <Icone nom="hourglass_top" taille={18} /> Collecte en cours : la tâche se fermera seule si
          c’est réglé.
        </p>
      )}
      {message && <p className="discret">{message}</p>}
      {refus.map((x) => (
        <p key={x} className="alerte">
          {x}
        </p>
      ))}
    </section>
  )
}
